import { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';

const OrderConfirmation = () => {
    const navigate = useNavigate();
    const { clearCart } = useContext(CartContext);
    const [order, setOrder] = useState(null);

    useEffect(() => {
        const orderData = localStorage.getItem('lastOrder');
        if (!orderData) {
            navigate('/cart');
            return;
        }

        const parsedOrder = JSON.parse(orderData);
        setOrder(parsedOrder);

        // Clear cart after order is placed
        clearCart();

        // Clean up order from localStorage after 5 minutes
        setTimeout(() => {
            localStorage.removeItem('lastOrder');
        }, 300000);
    }, [navigate, clearCart]);

    const handleWhatsAppOrder = () => {
        if (!order) return;

        const phoneNumber = '254712345678'; // Replace with your business WhatsApp number

        // Format order details for WhatsApp
        let message = `*New Order - ${order.orderNumber}*\n\n`;
        message += `*Customer Details:*\n`;
        message += `Name: ${order.customer.name}\n`;
        message += `Phone: ${order.customer.phone}\n`;
        message += `Email: ${order.customer.email}\n\n`;

        message += `*Delivery Address:*\n`;
        message += `${order.customer.address}\n`;
        message += `${order.customer.city}\n\n`;

        if (order.customer.notes) {
            message += `*Notes:* ${order.customer.notes}\n\n`;
        }

        message += `*Order Items:*\n`;
        order.items.forEach(item => {
            message += `• ${item.name} x${item.quantity} - KSh ${(item.price * item.quantity).toFixed(2)}\n`;
        });

        message += `\n*Order Summary:*\n`;
        message += `Subtotal: KSh ${order.subtotal.toFixed(2)}\n`;
        message += `Delivery: KSh ${order.deliveryFee.toFixed(2)}\n`;
        message += `*Total: KSh ${order.total.toFixed(2)}*\n\n`;
        message += `Please confirm this order and provide payment details.`;

        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

        window.open(whatsappUrl, '_blank');
    };

    if (!order) {
        return <div className="container">Loading...</div>;
    }

    return (
        <div className="container order-confirmation-page">
            <div className="confirmation-card">
                <div className="confirmation-icon">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="#27ae60" />
                        <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </div>

                <h1>Order Placed Successfully!</h1>
                <p className="order-number">Order #{order.orderNumber}</p>

                <div className="confirmation-message">
                    <p>Thank you for your order! We've received your order details.</p>
                    <p>Click the button below to complete your order via WhatsApp and receive payment instructions.</p>
                </div>

                <button onClick={handleWhatsAppOrder} className="btn btn-whatsapp">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Complete Order via WhatsApp
                </button>

                <div className="order-details">
                    <h2>Order Details</h2>

                    <div className="detail-section">
                        <h3>Customer Information</h3>
                        <p><strong>Name:</strong> {order.customer.name}</p>
                        <p><strong>Email:</strong> {order.customer.email}</p>
                        <p><strong>Phone:</strong> {order.customer.phone}</p>
                    </div>

                    <div className="detail-section">
                        <h3>Delivery Address</h3>
                        <p>{order.customer.address}</p>
                        <p>{order.customer.city}</p>
                    </div>

                    {order.customer.notes && (
                        <div className="detail-section">
                            <h3>Order Notes</h3>
                            <p>{order.customer.notes}</p>
                        </div>
                    )}

                    <div className="detail-section">
                        <h3>Items Ordered</h3>
                        {order.items.map(item => (
                            <div key={item._id} className="order-item">
                                <span>{item.name} x{item.quantity}</span>
                                <span>KSh {(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>

                    <div className="detail-section order-total">
                        <div className="total-row">
                            <span>Subtotal:</span>
                            <span>KSh {order.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="total-row">
                            <span>Delivery Fee:</span>
                            <span>KSh {order.deliveryFee.toFixed(2)}</span>
                        </div>
                        <div className="total-row final">
                            <span>Total:</span>
                            <span>KSh {order.total.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <Link to="/shop" className="btn btn-continue-shopping">
                    Continue Shopping
                </Link>
            </div>
        </div>
    );
};

export default OrderConfirmation;
